/**
 * @fileoverview Painel de uma execução: o percurso do contato no desenho do
 * fluxo (na versão que a execução usou), o resumo, o último erro, as mensagens
 * enviadas com o estado de entrega, a linha do tempo, os links e as conversões.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Drawer, IconButton } from "@material-ui/core";
import { MessageSquare, X } from "lucide-react";
import api from "../../../services/api";
import { i18n } from "../../../translate/i18n";
import ExecutionPath from "./ExecutionPath";
import { StateChip, contactName, orderLabel, reasonLabel } from "./ExecutionsHistory";
import { EMPTY, formatClock, formatCurrency, formatDateTime, formatDuration, formatNumber } from "./format";

const t = (key, options) => i18n.t(`flowResults.${key}`, options);
const GENERIC_ERROR_TYPES = ["Error", "NodeError", "AxiosError"];

const Fact = ({ classes, label, value }) => (
  <div>
    <div className={classes.factLabel}>{label}</div>
    <div className={classes.factValue}>{value}</div>
  </div>
);

const seconds = (ms) => `${(Number(ms) / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} s`;

/** Texto de um evento da linha do tempo: o que aconteceu, o detalhe e o erro, se houve. */
const describeEvent = (event, nodeLabel) => {
  const data = event.data || {};
  const meta = [];
  if (data.latencyMs) meta.push(seconds(data.latencyMs));
  else if (data.ms) meta.push(seconds(data.ms));
  if (data.tokensIn || data.tokensOut) meta.push(t("detail.tokens", { count: formatNumber((data.tokensIn || 0) + (data.tokensOut || 0)) }));

  switch (event.type) {
    case "execution_started":
      return { text: t("detail.event.started") };
    case "execution_finished":
      return { text: t("detail.event.finished"), meta: [reasonLabel(data.reason)].filter(Boolean) };
    case "run":
      if (event.status === "error") return { text: t("detail.event.runFailed"), error: data.error };
      return {
        text: i18n.exists(`flowResults.detail.action.${data.action}`) ? t(`detail.action.${data.action}`) : t("detail.action.none"),
        meta,
      };
    case "node":
      return {
        text: nodeLabel(event.nodeId),
        meta: [event.status === "error" ? null : t("detail.event.nodeDone"), ...meta].filter(Boolean),
        error: event.status === "error" ? data.error || t("detail.event.nodeFailed") : null,
      };
    case "tool":
      return { text: t("detail.event.tool", { tool: data.tool || "" }), meta, error: data.error };
    case "link_clicked":
      return { text: t("detail.event.linkClicked") };
    case "conversion":
      return {
        text: t("detail.event.conversion"),
        meta: data.value !== null && data.value !== undefined ? [formatCurrency(data.value, data.currency)] : [],
      };
    default:
      return { text: event.type };
  }
};

const messageTimes = (message) =>
  [
    message.deliveredAt && `${t("detail.messageStatus.delivered")} ${formatClock(message.deliveredAt)}`,
    message.readAt && `${t("detail.messageStatus.read")} ${formatClock(message.readAt)}`,
    message.failedAt && `${t("detail.messageStatus.failed")} ${formatClock(message.failedAt)}`,
  ].filter(Boolean);

const DrawerSkeleton = ({ classes }) => (
  <div className={classes.drawerBody} aria-hidden="true">
    <div className={classes.skeleton} style={{ height: 52, margin: "16px 0" }} />
    <div className={classes.skeleton} style={{ height: 320, marginBottom: 16 }} />
    <div className={classes.skeleton} style={{ height: 120 }} />
  </div>
);

const ExecutionDrawer = ({ classes, workflowId, executionId, onClose }) => {
  const history = useHistory();
  const [detail, setDetail] = useState(null);
  const [failed, setFailed] = useState(null);

  useEffect(() => {
    if (!executionId) return undefined;
    let cancelled = false;
    setDetail(null);
    setFailed(null);
    api
      .get(`/workflows/${workflowId}/executions/${executionId}`)
      .then(({ data }) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err) => {
        if (!cancelled) setFailed(err.response?.status === 404 ? "notFound" : "load");
      });
    return () => {
      cancelled = true;
    };
  }, [workflowId, executionId]);

  const nodeLabel = useMemo(() => {
    const labels = {};
    (detail?.version?.nodes || []).forEach((node) => {
      labels[node.id] = node.data?.label || "";
    });
    return (nodeId) => labels[nodeId] || t("detail.unknownStep");
  }, [detail]);

  const execution = detail?.execution;
  const title = execution ? contactName(execution) : t("detail.title");
  // Envio por status de OS que não saiu de propósito (descadastro, prazo): motivo, não erro.
  const skipped = execution?.kind === "order_status" && execution?.status === "cancelled";

  return (
    <Drawer anchor="right" open={!!executionId} onClose={onClose} classes={{ paper: classes.drawerPaper }}>
      <div className={classes.drawerHeader}>
        <div style={{ minWidth: 0 }}>
          <div className={classes.drawerTitle}>{title}</div>
          {execution && (
            <div className={classes.drawerSub}>
              {execution.contact?.name && <span>{execution.contact.number}</span>}
              <StateChip classes={classes} status={execution.status} />
              <span className={classes.chip}>{t(`kind.${execution.kind}`)}</span>
              {execution.order && <span>{orderLabel(execution.order)}</span>}
              {reasonLabel(execution.endReason) && <span>{reasonLabel(execution.endReason)}</span>}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          {execution?.ticketUuid && (
            <button
              type="button"
              className={`${classes.secondaryBtn} ${classes.smallBtn}`}
              onClick={() => history.push(`/tickets/${execution.ticketUuid}`)}
            >
              <MessageSquare size={14} /> {t("detail.openConversation")}
            </button>
          )}
          <IconButton size="small" onClick={onClose} aria-label={t("close")} style={{ color: "inherit" }}>
            <X size={18} />
          </IconButton>
        </div>
      </div>

      {!detail && !failed && <DrawerSkeleton classes={classes} />}

      {failed && (
        <div className={classes.drawerBody}>
          <div className={`${classes.banner} ${classes.bannerError}`} style={{ marginTop: 16 }} role="alert">
            {t(failed === "notFound" ? "detail.notFound" : "errors.execution")}
          </div>
        </div>
      )}

      {detail && execution && (
        <div className={classes.drawerBody}>
          <div className={classes.block}>
            <div className={classes.facts}>
              <Fact classes={classes} label={t("detail.summary.startedAt")} value={formatDateTime(execution.startedAt)} />
              <Fact
                classes={classes}
                label={t("detail.summary.finishedAt")}
                value={execution.finishedAt ? formatDateTime(execution.finishedAt) : t("detail.summary.stillRunning")}
              />
              <Fact classes={classes} label={t("detail.summary.duration")} value={formatDuration(execution.durationMs)} />
              {execution.kind === "ai_agent" ? (
                <Fact classes={classes} label={t("detail.summary.runs")} value={formatNumber(execution.runCount)} />
              ) : (
                <Fact classes={classes} label={t("detail.messages")} value={formatNumber(detail.messages.length)} />
              )}
              {execution.order && <Fact classes={classes} label={t("detail.orderNumber")} value={execution.order.number} />}
              {execution.order?.status && <Fact classes={classes} label={t("detail.orderStatus")} value={execution.order.status} />}
            </div>
          </div>

          {execution.lastError && (
            <div className={classes.block}>
              <div className={classes.blockTitle}>
                {t(execution.status === "failed" ? "detail.error" : skipped ? "detail.reason" : "detail.lastError")}
              </div>
              <div className={skipped ? classes.reasonBox : classes.errorBox}>
                {execution.lastError.message ||
                  (execution.kind === "triage" ? t("detail.triageErrorHint") : execution.lastError.type || t("detail.unknownError"))}
                <div className={classes.errorMeta}>
                  {[
                    // O tipo só aparece quando diz algo ("Error" genérico não ajuda ninguém).
                    // O código do motivo ("opt_out") já está dito no texto; o código da Meta ("132015") ajuda o suporte.
                    execution.lastError.message &&
                      !GENERIC_ERROR_TYPES.includes(execution.lastError.type) &&
                      !reasonLabel(execution.lastError.type) &&
                      execution.lastError.type,
                    execution.lastError.at && formatDateTime(execution.lastError.at),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
            </div>
          )}

          <div className={classes.block}>
            <div className={classes.blockTitle}>
              {t("detail.path")}
              {detail.version && <span className={classes.blockNote}>{t("detail.version", { number: detail.version.number })}</span>}
            </div>
            {detail.version ? (
              <>
                <div className={classes.pathBox}>
                  <ExecutionPath version={detail.version} execution={execution} />
                </div>
                <div className={classes.legend}>
                  <span className={classes.legendItem}>
                    <span className={classes.dotOk} /> {t("detail.legend.done")}
                  </span>
                  <span className={classes.legendItem}>
                    <span className={classes.dotRunning} /> {t("detail.legend.current")}
                  </span>
                  <span className={classes.legendItem}>
                    <span className={classes.dotError} /> {t("detail.legend.error")}
                  </span>
                  <span className={classes.legendItem}>
                    <span className={classes.legendPending} /> {t("detail.legend.pending")}
                  </span>
                </div>
                {!detail.version.isLatest && <div className={classes.tdMuted} style={{ marginTop: 8 }}>{t("detail.versionOld")}</div>}
              </>
            ) : (
              <div className={classes.tdMuted}>{t("detail.noVersion")}</div>
            )}
          </div>

          <div className={classes.block}>
            <div className={classes.blockTitle}>{t("detail.messages")}</div>
            {detail.messages.length === 0 ? (
              <div className={classes.tdMuted}>{t("detail.noMessages")}</div>
            ) : (
              <div className={classes.list}>
                {detail.messages.map((message) => (
                  <div key={message.id} className={classes.listRow}>
                    <div className={classes.listTime}>{formatClock(message.sentAt)}</div>
                    <div className={classes.listMain}>
                      {message.body ? (
                        <div className={classes.messageBody}>{message.body}</div>
                      ) : (
                        <div className={classes.messageDeleted}>{t(message.mediaType ? "detail.media" : "detail.deletedMessage")}</div>
                      )}
                      <div className={classes.listMeta}>
                        {[t(`detail.messageStatus.${message.accepted ? "sent" : "notSent"}`), ...messageTimes(message)].join(" · ")}
                      </div>
                      {message.errorMessage && <div className={classes.listError}>{message.errorMessage}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {detail.links.length > 0 && (
            <div className={classes.block}>
              <div className={classes.blockTitle}>{t("detail.links")}</div>
              <div className={classes.list}>
                {detail.links.map((link) => (
                  <div key={link.id} className={classes.listRow}>
                    <div className={classes.listTime}>{link.firstClickedAt ? formatClock(link.firstClickedAt) : EMPTY}</div>
                    <div className={classes.listMain}>
                      <span className={classes.link}>{link.url}</span>
                      <div className={classes.listMeta}>
                        {link.clickCount > 0 ? t("detail.clicks", { count: link.clickCount }) : t("detail.notClicked")}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {detail.conversions.length > 0 && (
            <div className={classes.block}>
              <div className={classes.blockTitle}>{t("detail.conversions")}</div>
              <div className={classes.list}>
                {detail.conversions.map((conversion) => (
                  <div key={conversion.id} className={classes.listRow}>
                    <div className={classes.listTime}>{formatClock(conversion.occurredAt)}</div>
                    <div className={classes.listMain}>
                      {t("detail.order", { id: conversion.externalId })}
                      {conversion.value !== null && ` · ${formatCurrency(conversion.value, conversion.currency)}`}
                      <div className={classes.listMeta}>
                        {formatDateTime(conversion.occurredAt)}
                        {conversion.status === "reverted" && ` · ${t("detail.reverted")}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={classes.block}>
            <div className={classes.blockTitle}>{t("detail.timeline")}</div>
            {detail.events.length === 0 ? (
              <div className={classes.tdMuted}>{t("detail.noEvents")}</div>
            ) : (
              <div className={classes.list}>
                {detail.events.map((event) => {
                  const described = describeEvent(event, nodeLabel);
                  return (
                    <div key={event.id} className={classes.listRow}>
                      <div className={classes.listTime} title={formatDateTime(event.occurredAt)}>
                        {formatClock(event.occurredAt)}
                      </div>
                      <div className={classes.listMain}>
                        {described.text}
                        {described.meta?.length > 0 && <div className={classes.listMeta}>{described.meta.join(" · ")}</div>}
                        {described.error && <div className={classes.listError}>{described.error}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default ExecutionDrawer;
